import i18n from '../../../i18n'
import { isNullOrUndefined } from 'is-what'
import { removeDuplicates } from '../../../utils'
import { LineChartLabel, RatingHistory, WorldTennisNumber } from 'types/global'
import { WTN_SCALE_HIGH, WTN_SCALE_LOW } from 'constants/global'

export function createDataset({
  label,
  hex,
  data,
  isHidden,
  isDesktop,
}: {
  label: string
  hex: string
  data: any[]
  isHidden: boolean
  isDesktop: boolean
}) {
  const dataset = data.length > 0 && data[0] === null ? [null, null] : data
  label += data.length ? '' : ': ' + i18n.t('noDataShort', { ns: 'strCharts' })

  return {
    label,
    fill: false,
    lineTension: 0.1,
    borderCapStyle: 'butt',
    borderJoinStyle: 'miter',
    pointBorderWidth: 1,
    pointHoverRadius: 6,
    pointHoverBorderWidth: 3,
    pointRadius: 0,
    borderWidth: isDesktop ? 2 : 1,
    pointHitRadius: 12,
    pointBackgroundColor: hex,
    backgroundColor: hex,
    borderColor: hex,
    pointBorderColor: hex,
    pointHoverBackgroundColor: '#FFF',
    pointHoverBorderColor: hex,
    data: dataset,
    hidden: isHidden,
    steppedLine: true,
  }
}

//Replace null values if they are at the first or last entry of the array.
//spanGaps does not work in the chart if there is no value to interpolate
export const isNotNullOrUndefined = d => !isNullOrUndefined(d)

export const checkNullValues = (data: any[]) => {
  const findStartingVal = (arr: any[]) => arr.find(isNotNullOrUndefined) || null
  return data && data.length > 2
    ? [findStartingVal(data), ...data.slice(1, -1), findStartingVal(data.slice().reverse())]
    : data
}

export const getDataKeyFromLabel = (label: LineChartLabel) => `${label.type}.${label.keyCol}`

export const formatData = ({
  labels,
  ratingsData,
  startDate,
  endDate,
}: {
  labels: LineChartLabel[]
  ratingsData: RatingHistory[]
  startDate: string
  endDate: string
}) => {
  //To perform ops on
  let labelledData: { [k: string]: WorldTennisNumber[] } = {}

  //To return formatted
  let ratings: { [k: string]: (string | null)[] } = {}

  //Assign ratings data to the correct label - either player name or doubles/singles
  labels.forEach((label, index) => {
    const dataIndex = Math.floor(index / 2)
    const dataKey = getDataKeyFromLabel(label)
    ratings[dataKey] = []
    labelledData[dataKey] = [...ratingsData[dataIndex][label.type]]
  })

  //Get all dates from records
  const dates = Object.values(labelledData)
    .flatMap(d => Object.values(d).map(i => i.ratingDate))
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

  //Me array of dates into one, sort, and remove any dups
  const uniqueDates: string[] = removeDuplicates(dates)

  //Make sure startDate and endDate are included in dates array
  if (uniqueDates.length && new Date(uniqueDates[0]) > new Date(startDate)) {
    uniqueDates.unshift(startDate)
  }

  if (uniqueDates.length && new Date(uniqueDates[uniqueDates.length - 1]) < new Date(endDate)) {
    uniqueDates.push(endDate)
  }

  uniqueDates.forEach(date => {
    for (let key in labelledData) {
      let found = labelledData[key].find(i => {
        return i.ratingDate === date
      })

      ratings[key].push(found ? found.tennisNumber.toFixed(2) : null)
    }
  })

  //Make there are no null values at the first or last index of array
  //(Graph will not interpolate to edges of graph if so)
  Object.entries(ratings).forEach(([key, arr]) => (ratings[key] = checkNullValues(arr)))

  return { ratings: ratings, dates: uniqueDates }
}

const scaleConfigs: { scale: number; step: number }[] = [
  { scale: 40, step: 5 },
  { scale: 20, step: 2.5 },
  { scale: 15, step: 2.5 },
  { scale: 10, step: 1 },
  { scale: 5, step: 0.5 },
  { scale: 2, step: 0.2 },
  { scale: 1, step: 0.1 },
  { scale: -1, step: 0 },
]

const getAxisSettings = (config: {
  minRating: number
  maxRating: number
  step: number
  minLimit?: number
  maxLimit?: number
}) => {
  const { minRating, maxRating, step, minLimit = WTN_SCALE_HIGH, maxLimit = WTN_SCALE_LOW } = config
  const MIN_TICKS_MODIFIER = 5
  let min = 0
  while (min + step < minRating) {
    min += step
  }

  let max = min
  while (max <= maxRating) {
    max += step
  }

  if (max > maxLimit) max = maxLimit
  if (min < minLimit) min = minLimit

  return {
    min,
    max,
    ticks: Math.ceil((max - min) / step) + MIN_TICKS_MODIFIER,
  }
}

export const getYAxisBounds = (data: (string | null)[]) => {
  const orderedData = data
    .flat()
    .filter(Boolean)
    .map(rating => Number(rating))
    .sort((a, b) => a - b)

  if (!orderedData.length) return { min: WTN_SCALE_HIGH, max: WTN_SCALE_LOW, ticks: 10, step: 5 }

  const diff = orderedData[orderedData.length - 1] - orderedData[0]
  const selectedConfigIndex = scaleConfigs.findIndex(({ scale }) => scale < diff) - 1
  const { step } = scaleConfigs[selectedConfigIndex]
  const { min, max, ticks } = getAxisSettings({
    minRating: orderedData[0],
    maxRating: orderedData[orderedData.length - 1],
    step,
  })

  return { min, max, ticks, step }
}
